import { useState } from "react";

interface SmartImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Text shown as a faint watermark on the fallback panel when the image fails to load. */
  fallbackText?: string;
  /** If true, render nothing on failure instead of a placeholder panel (for decorative background images). */
  hideOnError?: boolean;
}

/**
 * Drop-in replacement for `<img>` that degrades gracefully when the source
 * can't be reached (broken link, offline preview, blocked network) instead
 * of showing the browser's default broken-image icon. On failure it shows
 * an on-brand navy/silver placeholder panel — optionally labelled with the
 * item's own text (category, title) — or nothing at all for decorative
 * background imagery.
 */
export default function SmartImage({
  src,
  alt,
  className = "",
  loading = "lazy",
  fallbackText,
  hideOnError = false,
}: SmartImageProps) {
  const [failed, setFailed] = useState(!src);

  if (failed) {
    if (hideOnError) return null;
    return (
      <div
        className={`relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 ${className}`}
        role="img"
        aria-label={alt}
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "16px 16px",
          }}
        />
        {fallbackText && (
          <span className="relative z-10 px-6 text-center font-serif font-semibold text-white/25 text-xl md:text-2xl leading-snug select-none">
            {fallbackText}
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setFailed(true)}
    />
  );
}
